import { ValidationTracker } from 'components/domain/ValidationTracker/ValidationTracker';
import { Form, Formik, FormikProps } from 'formik';
import { RefObject, useEffect, useRef, useState } from 'react';
import {
  borrowerLoanFormConfig,
  BorrowerLoanFormValues
} from './borrowerLoanForm';
import { BorrowerLoanFormLayout } from './BorrowerLoanFormLayout';

interface BorrowerLoanFormProps {
  initialValues?: BorrowerLoanFormValues;
  onSubmit: (values: BorrowerLoanFormValues) => void | Promise<void>;
  onValidationChange: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  formikRef: RefObject<FormikProps<BorrowerLoanFormValues>>;
  isRenego: boolean;
  disabledFields?: Array<Exclude<keyof BorrowerLoanFormValues, 'id'>>;
  isSubmitting?: boolean;
  showInlineSaveButton?: boolean;
}

const DirtyTracker = ({
  dirty,
  onDirtyChange,
  initialValuesId
}: {
  dirty: boolean;
  onDirtyChange: (isDirty: boolean) => void;
  initialValuesId?: string;
}) => {
  const prevInitialValuesId = useRef(initialValuesId);
  const hasUserInteracted = useRef(false);

  // Reset when initialValues change (tab switch or data reload)
  useEffect(() => {
    if (prevInitialValuesId.current !== initialValuesId) {
      prevInitialValuesId.current = initialValuesId;
      hasUserInteracted.current = false;
      onDirtyChange(false);
    }
  }, [initialValuesId, onDirtyChange]);

  // Track dirty state after user interaction
  useEffect(() => {
    // If dirty becomes true, user has interacted
    if (dirty) {
      hasUserInteracted.current = true;
      onDirtyChange(true);
    } else if (hasUserInteracted.current) {
      // Only report false if user has previously interacted
      onDirtyChange(false);
    }
  }, [dirty, onDirtyChange]);

  return null;
};

export const BorrowerLoanForm: React.FC<BorrowerLoanFormProps> = ({
  initialValues = borrowerLoanFormConfig.initialValues,
  onSubmit,
  onValidationChange,
  onDirtyChange,
  formikRef,
  isRenego,
  disabledFields,
  isSubmitting,
  showInlineSaveButton = false
}) => {
  const [showSaveButton, setShowSaveButton] = useState(false);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={borrowerLoanFormConfig.validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ isValid, dirty, submitForm }) => (
        <>
          <ValidationTracker
            isValid={isValid}
            onValidationChange={onValidationChange}
          />
          <DirtyTracker
            dirty={dirty}
            onDirtyChange={(isDirty) => {
              setShowSaveButton(isDirty && isValid);
              onDirtyChange?.(isDirty);
            }}
            initialValuesId={initialValues?.id}
          />
          <Form>
            <BorrowerLoanFormLayout
              isRenego={isRenego}
              disabledFields={disabledFields}
              showSaveButton={showInlineSaveButton && showSaveButton && isValid}
              isSubmitting={isSubmitting}
              onSave={submitForm}
            />
          </Form>
        </>
      )}
    </Formik>
  );
};
